import { Box } from '@mui/material';
import React, { useContext } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { formatChartDate, calculateYAxisConfig } from '../../helpers/chartHelpers';
import { ToolContext } from '../../../../../core/context/ToolContext';
import { SnakeLoader } from '../../../../../core/loader/SnakeLoader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

export const ContactInteractionChart = () => {
  const { social_media_insight_state } = useContext(ToolContext)
  const contentInteraction = social_media_insight_state.insightData.data?.content_interaction || [];
  const labels = contentInteraction.map(item => formatChartDate(item.date, item.end_date));
  const allValues = contentInteraction.flatMap(item => [
    item.views,
    item.likes,
    item.comments
  ]);
  const yAxisConfig = calculateYAxisConfig(allValues);
  const createGradient = (ctx, colorStart, colorEnd) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, colorStart);
    gradient.addColorStop(1, colorEnd);
    return gradient;
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#061D22',
        padding: 12,
        cornerRadius: 18,
        displayColors: false,
        titleFont: {
          size: 0
        },
        bodyFont: {
          size: 14,
          weight: '600'
        },
        callbacks: {
          title: () => '',
          label: (context) => {
            const label = context.dataset.label;
            const value = context.parsed.y;
            return `${value} ${label.toLowerCase()}`;
          },
        },
        position: 'nearest',
        intersect: false,
        mode: 'index',
      }
    },
    scales: {
      y: {
        ...yAxisConfig,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: 'Inter',
            size: 11
          }
        }
      }
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 2,
      },
      point: {
        radius: 0,
        hoverRadius: 6,
      }
    },
    interaction: {
      intersect: false,
      mode: 'index',
    }
  };

  const datasets = [
    {
      label: 'Views',
      data: contentInteraction.map(item => item.views),
      borderColor: '#1BC2FF',
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        return createGradient(ctx, '#1BC2FFFF', '#1BC2FF00');
      },
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: '#1BC2FF',
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
      order: 1
    },
    {
      label: 'Likes',
      data: contentInteraction.map(item => item.likes),
      borderColor: '#FF7664',
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        return createGradient(ctx, '#FF7664FF', '#FF766400');
      },
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: '#FF7664',
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
      order: 2
    },
    {
      label: 'Comments',
      data: contentInteraction.map(item => item.comments),
      borderColor: '#001DFF',
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        return createGradient(ctx, '#ADB7F9FF', '#B1B9F800');
      },
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: '#001DFF',
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
      order: 3
    },
  ];

  const data = {
    labels,
    datasets
  };

  const legendItems = datasets.map(dataset => ({
    color: dataset.borderColor,
    label: dataset.label
  }));

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }}>
        <Box fontSize={18} fontWeight={700}>
          Content Interaction
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 3,
          alignItems: 'center'
        }}>
          {legendItems.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '12px',
                color: '#666'
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: item.color
                }}
              />
              {item.label}
            </Box>
          ))}
        </Box>
      </Box>
      {(social_media_insight_state.insightLoading.data || social_media_insight_state.topContentsLoading.data) ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px', 
          width: '100%', 
          fontSize: '14px', 
          fontWeight: '600',
          color: '#666'
        }}>
          <SnakeLoader size="1rem" bg="#11783C" distance="0.3rem" />
        </Box>
      ) : (
        <Box sx={{ height: '300px', width: '100%', position: 'relative' }}>
          {contentInteraction.length > 0 && !contentInteraction.every(m => m.views === 0 && m.likes === 0 && m.comments === 0) ? (
            <Line options={options} data={data} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
              No data available
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};